import { TFunction } from "@/types";
import { DateTime, Interval } from "luxon";
import { TIME_CONSTANTS } from "@/utils/constants";

export const formatEpochTime = (
  epochTime: number,
  timeZone: string,
  showDate: boolean,
  showDateWithTomorrow: boolean,
  t: TFunction,
  locale: string = "en-US"
) => {
  if (!epochTime) {
    return "";
  }
  const dt = DateTime.fromMillis(epochTime).setZone(timeZone).setLocale(locale);
  const now = DateTime.now().setZone(timeZone);

  let dayLabel;

  if (showDate) {
    dayLabel = dt.toFormat("MMM d");
  } else if (dt.hasSame(now, "day")) {
    dayLabel = t("today");
  } else if (dt.hasSame(now.plus({ days: 1 }), "day")) {
    dayLabel = showDateWithTomorrow
      ? t("tomorrowDate", {
          formattedDate: dt.toFormat("MMM d"),
        })
      : t("tomorrow");
  } else {
    dayLabel = dt.toFormat("MMM d");
  }

  // This automatically switches between "3:45 PM" (US) and "15 h 45" (FR)
  const timeLabel = dt.toLocaleString(DateTime.TIME_SIMPLE);

  return t("dateTime", { day: dayLabel, time: timeLabel });
};

export const canScheduleOrder = (
  daysHours: any,
  now: DateTime,
  timezone: string,
  leadTime: number | undefined,
  isToday?: boolean
) => {
  if (!daysHours) return false;

  return daysHours
    .flatMap((hours: { start: any; end: any }) => {
      const startTime = toDateTime(hours.start, timezone);
      const endTime = toDateTime(hours.end, timezone);

      const interval = Interval.fromDateTimes(startTime, endTime);

      //If current time is already past the business hours today, cannot schedule
      if (isToday && interval.isBefore(now)) return false;
      /**
       * If Business hours start time is future time, then check if the business hours interval for today is enough
       * to prepare the order and keep it ready for schedule
       * Add lead time to start time + 15 minutes and compare the intervals
       */
      if (!isToday || interval.isAfter(now)) {
        if (interval.length("minutes") < (leadTime || 0) + 15) return false;

        return true;
      }
      //If current time is within today's business hours check if order can be prepared before closing time
      if (interval.contains(now))
        return interval.contains(
          roundToNext15Minutes(now.plus({ minutes: leadTime }))
        );

      return false;
    })
    .includes(true);
};

export const roundToNext15Minutes = (dateTime: DateTime): DateTime => {
  const minutes = dateTime.minute;
  const remainder = minutes % 15;
  if (remainder === 0) return dateTime.startOf("minute");
  return dateTime.plus({ minutes: 15 - remainder }).startOf("minute");
};

export const toDateTime = (time: string, timezone?: string) =>
  DateTime.fromObject(
    {
      hour: getHour(time),
      minute: getMinutes(time),
    },
    { zone: timezone }
  );

/** Normalizes a `Date | string` union to a native `Date` object. */
export const toNativeDate = (v: Date | string): Date =>
  v instanceof Date ? v : new Date(v);

export const HHMMtoDate = (
  HHMM: string,
  timezone: string,
  date: Date
): DateTime => {
  const [hours, minutes] = [HHMM.slice(0, 2), HHMM.slice(2, 4)];
  const dateInMerchantZone = DateTime.fromJSDate(date, { zone: timezone });
  return dateInMerchantZone.set({
    hour: parseInt(hours, 10),
    minute: parseInt(minutes, 10),
    second: 0,
    millisecond: 0,
  });
};

const getHour = (time: string) => Math.floor(parseInt(time) / 100);
const getMinutes = (time: string) => parseInt(time) % 100;

export const addMinutes = (time: number, minutesToAdd: number) => {
  if (!minutesToAdd || minutesToAdd <= 0) return time;

  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  const totalMinutes = hours * TIME_CONSTANTS.MINUTES_PER_HOUR + minutes;
  const newTotalMinutes = totalMinutes + minutesToAdd;

  const newHours =
    Math.floor(newTotalMinutes / TIME_CONSTANTS.MINUTES_PER_HOUR) %
      TIME_CONSTANTS.HOURS_PER_DAY || TIME_CONSTANTS.HOURS_PER_DAY;
  const newMinutes = newTotalMinutes % TIME_CONSTANTS.MINUTES_PER_HOUR;

  return Math.min(2400, newHours * 100 + newMinutes);
};

export const getFormattedScheduledOrderDisplay = (
  isToday: boolean,
  isTomorrow: boolean,
  formattedDate: string,
  formattedTime: string,
  tUtils: TFunction
) => {
  let displayString;
  if (isToday) {
    displayString = tUtils("todayTime", { formattedTime });
  } else if (isTomorrow) {
    displayString = tUtils("tomorrowDateTime", {
      formattedDate,
      formattedTime,
    });
  } else {
    displayString = `${formattedDate}, ${formattedTime}`;
  }
  return displayString;
};

/**
 * Converts a time string (e.g. "1100") into a Luxon DateTime object set to a timezone or UTC.
 */
export const convertTimeStringToDate = (
  timeStr: string,
  timezone: string = "utc"
) => {
  const timeInt = Number.parseInt(timeStr, 10);
  if (Number.isNaN(timeInt)) return DateTime.now().setZone(timezone);

  const hours = Math.floor(timeInt / 100);
  const minutes = timeInt % 100;

  return DateTime.fromObject(
    { hour: hours, minute: minutes },
    { zone: timezone }
  );
};
